import React, { useState, useEffect, useRef } from 'react';
import { Search, RotateCcw, X, Clock } from 'lucide-react';

interface SearchWithHistoryProps {
  storageKey: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onSearch: (value: string) => void;
  onReset: () => void;
}

const MAX_HISTORY = 10;

export default function SearchWithHistory({
  storageKey,
  placeholder,
  value,
  onChange,
  onSearch,
  onReset,
}: SearchWithHistoryProps) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) setHistory(JSON.parse(stored));
    } catch {}
  }, [storageKey]);

  const saveToHistory = (term: string) => {
    if (!term.trim()) return;
    const updated = [term.trim(), ...history.filter(h => h !== term.trim())].slice(0, MAX_HISTORY);
    setHistory(updated);
    try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch {}
  };

  const removeFromHistory = (term: string) => {
    const updated = history.filter(h => h !== term);
    setHistory(updated);
    try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch {}
  };

  const clearHistory = () => {
    setHistory([]);
    try { localStorage.removeItem(storageKey); } catch {}
  };

  const handleFocus = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setShowHistory(true);
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => {
      setShowHistory(false);
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveToHistory(value);
      onSearch(value);
      setShowHistory(false);
    }
  };

  const handleHistoryClick = (term: string) => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    onChange(term);
    onSearch(term);
    setShowHistory(false);
    inputRef.current?.blur();
  };

  const handleSearchBtnClick = () => {
    saveToHistory(value);
    onSearch(value);
  };

  return (
    <div className="flex-1 relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
      />

      {showHistory && history.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden"
        >
          <div className="px-3 py-2 text-xs text-gray-400 font-medium border-b border-gray-100 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            搜索历史
          </div>
          <div className="max-h-[240px] overflow-y-auto">
            {history.map((term) => (
              <div
                key={term}
                className="flex items-center justify-between px-3 py-2 hover:bg-blue-50 cursor-pointer group transition-colors"
                onMouseDown={(e) => { e.preventDefault(); handleHistoryClick(term); }}
              >
                <span className="text-sm text-gray-700 truncate flex-1">{term}</span>
                <button
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); removeFromHistory(term); }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 rounded transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="px-3 py-2 border-t border-gray-100">
            <button
              onMouseDown={(e) => { e.preventDefault(); clearHistory(); }}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              清空历史
            </button>
          </div>
        </div>
      )}
    </div>
  );
}